import React from "react";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable, UniqueIdentifier } from "@dnd-kit/core";
import SurveyQuestionItem from "./SortableItem";
import SortableHeader from "./SortableHeader";
import { FlattenedPickedQuestion, PickedQuestion } from "api/sms-api";

const countQuestions = (
  items: Array<PickedQuestion | FlattenedPickedQuestion>
): number => {
  return items.reduce((count, item) => {
    const selfCount =
      item.formType !== "multi_question" && item.formType !== "group" ? 1 : 0;
    return count + selfCount + countQuestions(item.children || []);
  }, 0);
};

interface SelectItemsProps {
  categoryId: UniqueIdentifier;
  items: Array<FlattenedPickedQuestion>;
  overId: string | null;
  sortDirection: "before" | "after" | null;

  handleClickDisplayRequired: (index: number) => void;
  handleClickInputRequired: (index: number) => void;
  handleClickSurveyOnly: (index: number) => void;
  deleteSelectedQuestions: (index: number) => void;
  handleClickQuestion: (index: number) => void;
  handleClickArchive: (index: number) => void;
  handleCollapse: (index: UniqueIdentifier) => void;
  duplicateQuestion: (index: number) => void;
}

const SelectItems: React.FC<SelectItemsProps> = (props) => {
  const { setNodeRef } = useDroppable({ id: props.categoryId });

  // 親要素の表示用インデックスを計算する関数
  const getParentDisplayIndex = (
    value: FlattenedPickedQuestion
  ): number | undefined => {
    const parentItem = props.items.find(
      (item) => item.selectId === value.parentId
    );
    if (!parentItem) return undefined;

    // 親要素が最上位の場合は、最上位要素での順序を計算
    if (parentItem.depth === 0) {
      const topLevelItems = props.items
        .filter((item) => item.depth === 0)
        .sort((a, b) => (a.index || 0) - (b.index || 0));
      const parentDisplayIndex = topLevelItems.findIndex(
        (item) => item.selectId === parentItem.selectId
      );

      return parentDisplayIndex !== -1 ? parentDisplayIndex : undefined;
    }

    // 親要素が子要素の場合も、同じ親を持つ兄弟要素内での順序を計算
    if (parentItem.parentId) {
      const siblings = props.items
        .filter((item) => item.parentId === parentItem.parentId)
        .sort((a, b) => (a.index || 0) - (b.index || 0));
      const siblingIndex = siblings.findIndex(
        (item) => item.selectId === parentItem.selectId
      );
      return siblingIndex !== -1 ? siblingIndex : undefined;
    }

    return parentItem.index;
  };

  const getParentIndex = (value: FlattenedPickedQuestion) => {
    return getParentDisplayIndex(value);
  };

  const getGrandParentIndex = (value: FlattenedPickedQuestion) => {
    const parentItem = props.items.find(
      (item) => item.selectId === value.parentId
    );

    if (!parentItem) return undefined;

    return getParentDisplayIndex(parentItem);
  };

  // 複数質問のselectId配列
  const multiQuestionIds = props.items
    .filter((item) => item.formType === "multi_question" && item.selectId)
    .map((item) => item.selectId as string);

  // 分岐選択肢のselectId
  const branchIds = new Set(
    props.items
      .filter(
        (item) =>
          (item.formType === "branch" || item.formType === "branch_option") &&
          item.selectId
      )
      .map((item) => item.selectId as string)
  );

  // 分岐下のグループのselectId
  const branchGroupIds = new Set(
    props.items
      .filter(
        (item) =>
          item.formType === "group" &&
          item.parentId &&
          branchIds.has(item.parentId) &&
          item.selectId
      )
      .map((item) => item.selectId as string)
  );

  // 表示必須は階層が1層のもの + 分岐後の設問もカウント対象
  const displayCount = props.items.filter(
    (item) =>
      (item.displayRequired && item.depth === 0) ||
      (item.displayRequired &&
        item.parentId &&
        branchGroupIds.has(item.parentId) &&
        item.formType !== "group")
  ).length;
  // 入力必須とアンケ専用は1層と2層目がグループの場合カウント対象
  const inputCount = props.items.filter(
    (item) =>
      (item.inputRequired && item.depth === 0) ||
      (item.inputRequired && item.depth === 1 && item.formType === "group") ||
      // 複数設問の子要素（質問グループ以外）もカウント対象に追加
      (item.inputRequired &&
        item.parentId &&
        multiQuestionIds.includes(item.parentId) &&
        item.formType !== "group")
  ).length;
  const surveyOnlyCount = props.items.filter(
    (item) =>
      (item.surveyOnly && item.depth === 0) ||
      (item.surveyOnly && item.depth === 1 && item.formType === "group") ||
      // 複数設問の子要素（質問グループ以外）もカウント対象に追加
      (item.surveyOnly &&
        item.parentId &&
        multiQuestionIds.includes(item.parentId) &&
        item.formType !== "group")
  ).length;
  // 不採用は全てカウント対象
  const rejectedCount = props.items.filter((item) => item.archive).length;

  // アンケート項目は設問のみカウント（質問グループ・複数設問・分岐下の選択肢グループは除外）
  // 閉じた項目の子も含めて、最上位からツリー全体を数える
  const questionCount = countQuestions(
    props.items.filter((item) => item.depth === 0)
  );

  return (
    <SortableContext items={props.items} strategy={verticalListSortingStrategy}>
      <SortableHeader
        itemLength={questionCount}
        displayCount={displayCount}
        inputCount={inputCount}
        surveyOnlyCount={surveyOnlyCount}
        rejectedCount={rejectedCount}
      ></SortableHeader>
      <div
        ref={setNodeRef}
        style={{
          height: "100%",
          maxHeight: "1090px",
          overflowY: "auto",
          position: "relative",
        }}
      >
        {props.overId === props.categoryId && props.sortDirection === "before" && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              backgroundColor: "#1890ff",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />
        )}
        {props.items.map((value, index) => (
          <div key={value.selectId} style={{ position: "relative" }}>
            {props.overId === value.selectId &&
              props.sortDirection === "before" && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: `${value.depth * 50}px`,
                    right: 0,
                    height: "2px",
                    backgroundColor: "#1890ff",
                    zIndex: 1,
                    pointerEvents: "none",
                  }}
                />
              )}

            <SurveyQuestionItem
              value={value}
              index={index}
              parentIndex={getParentIndex(value)}
              grandParentIndex={getGrandParentIndex(value)}
              multiQuestionIds={multiQuestionIds}
              allItems={props.items}
              itemLength={props.items.length}
              displayCount={displayCount}
              inputCount={inputCount}
              surveyOnlyCount={surveyOnlyCount}
              rejectedCount={rejectedCount}
              handleClickDisplayRequired={() =>
                props.handleClickDisplayRequired(index)
              }
              handleClickInputRequired={() =>
                props.handleClickInputRequired(index)
              }
              handleClickSurveyOnly={() => props.handleClickSurveyOnly(index)}
              deleteSelectedQuestions={() =>
                props.deleteSelectedQuestions(index)
              }
              handleClickQuestion={() => props.handleClickQuestion(index)}
              handleClickArchive={() => props.handleClickArchive(index)}
              collapsed={Boolean(value.collapsed && value.children.length)}
              handleCollapse={
                value.children.length
                  ? () => props.handleCollapse(value.selectId || "")
                  : undefined
              }
              duplicateQuestion={() => props.duplicateQuestion(index)}
            />

            {props.overId === value.selectId &&
              props.sortDirection === "after" && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: `${value.depth * 50}px`,
                    right: 0,
                    height: "2px",
                    backgroundColor: "#1890ff",
                    zIndex: 1,
                    pointerEvents: "none",
                  }}
                />
              )}
          </div>
        ))}
      </div>
    </SortableContext>
  );
};

export default SelectItems;
